type ClarityFn = (...args: unknown[]) => void;

const getClarity = (): ClarityFn | null => {
  if (typeof window === 'undefined') return null;
  const clarity = (window as unknown as {clarity?: unknown}).clarity;
  return typeof clarity === 'function' ? (clarity as ClarityFn) : null;
};

export const isClarityReady = (): boolean => getClarity() !== null;

type ClarityStub = ClarityFn & {q?: unknown[][]};

export const ensureClarityLoaded = (projectId: string | undefined): void => {
  if (typeof window === 'undefined' || !projectId) return;
  const w = window as unknown as {
    clarity?: ClarityStub;
    __clarityInjected?: boolean;
  };
  if (getClarity() || w.__clarityInjected) return;
  w.__clarityInjected = true;
  try {
    const stub = function (...args: unknown[]) {
      (stub.q = stub.q || []).push(args);
    } as ClarityStub;
    w.clarity = w.clarity || stub;
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.clarity.ms/tag/' + projectId;
    const first = document.getElementsByTagName('script')[0];
    first?.parentNode?.insertBefore(script, first);
  } catch (e) {
    w.__clarityInjected = false;
  }
};

export const isClarityPathEligible = (path: string): boolean => {
  if (typeof window === 'undefined') return false;
  const criteria = (
    window as unknown as {
      __FEATURE_FLAGS__?: {
        clarityCriteria?: {enableClarity?: boolean; pageNames?: string[]};
      };
    }
  ).__FEATURE_FLAGS__?.clarityCriteria;
  if (!criteria?.enableClarity) return false;
  const pageNames = criteria.pageNames;
  if (Array.isArray(pageNames) && pageNames.length) {
    return pageNames.some(prefix => path.startsWith(prefix));
  }
  return true;
};

export const clarityEvent = (eventName: string) => {
  const clarity = getClarity();
  if (!clarity || !eventName) return;
  try {
    clarity('event', eventName);
  } catch (e) {
    console.log('Clarity event error', e);
  }
};

export const claritySetTag = (key: string, value: string | string[]) => {
  const clarity = getClarity();
  if (!clarity || !key || value === undefined || value === null || value === '')
    return;
  try {
    clarity('set', key, value);
  } catch (e) {
    console.log('Clarity set tag error', e);
  }
};

export const clarityIdentify = (
  customId: string,
  customSessionId?: string,
  customPageId?: string,
  friendlyName?: string
) => {
  const clarity = getClarity();
  if (!clarity || !customId) return;
  try {
    clarity('identify', customId, customSessionId, customPageId, friendlyName);
  } catch (e) {
    console.log('Clarity identify error', e);
  }
};

const resolveClarityUserId = (): string => {
  try {
    const userId = sessionStorage.getItem('userId');
    if (userId && Number(userId) > 0) return userId;
  } catch {}
  return '';
};

let lastIdentifiedId = '';

export const clarityIdentifyUser = () => {
  if (!isClarityReady()) return;
  const id = resolveClarityUserId();
  if (!id || id === lastIdentifiedId) return;
  lastIdentifiedId = id;
  clarityIdentify(id, undefined, undefined, id);
};
